import { useMemo } from "react";

import moment from "moment";

import { useUser } from "@components/user-context";
import { useAdoptedBudgetMix } from "@features/mmm/hooks/use-adopted-budget-mix";
import { useYesterday } from "@hooks/use-yesterday";
import { type FetchLiveForecastTrackingData_me_mmm_performanceTracking_liveForecast } from "@north-beam/nb-client-side";

import { accumulateYValues } from "../../chart-accumulator";
import {
  ACTUAL_COLOR,
  ACTUAL_ID,
  LIVE_FORECAST_BORDER_DASH,
  LIVE_FORECAST_COLOR,
  LIVE_FORECAST_ID,
  PLANNING_TARGET_COLOR,
  PLANNING_TARGET_ID,
  TARGET_RANGE_COLOR,
  TARGET_RANGE_ID,
} from "../const";

type KpiDataAPIDatum = {
  planningTarget: number | null;
  targetRange: {
    low: number | null;
    median: number | null;
    high: number | null;
  };
  weeklyMetrics: {
    dateRange: {
      startDate: string;
      endDate: string;
    };
    actual: number | null;
    planningTarget: number | null;
    targetRange: {
      low: number | null;
      median: number | null;
      high: number | null;
    };
  }[];
  dailyMetrics: {
    date: string;
    actual: number | null;
    planningTarget: number | null;
    targetRange: {
      low: number | null;
      median: number | null;
      high: number | null;
    };
  }[];
};

export function useBuildKpiDataset({
  data,
  datasetVisibility,
}: {
  data: KpiDataAPIDatum;
  datasetVisibility: Record<string, boolean>;
}) {
  const yesterday = useYesterday();

  const dateLabels =
    data.dailyMetrics.map(
      ({ date }) => `${moment(date).format("MMM DD, YYYY")}`,
    ) ?? [];

  const { actualData, planningHighTarget, planningLowTarget, planningTarget } =
    useMemo(() => {
      const _actualData: {
        y: number | null;
        x: number;
      }[] = [];
      const _planningTarget: {
        y: number;
        x: number;
      }[] = [];

      const _planningLowTarget: {
        y: number;
        x: number;
      }[] = [];
      const _planningHighTarget: {
        y: number;
        x: number;
      }[] = [];

      data.dailyMetrics.forEach((row, index) => {
        if (moment(row.date).isSameOrBefore(yesterday)) {
          _actualData.push({
            y: row.actual,
            x: index,
          });
        }
        _planningTarget.push({
          y: row.planningTarget ?? 0,
          x: index,
        });
        _planningLowTarget.push({
          y: row.targetRange.low ?? 0,
          x: index,
        });
        _planningHighTarget.push({
          y: row.targetRange.high ?? 0,
          x: index,
        });
      });
      return {
        actualData: accumulateYValues(_actualData),
        planningTarget: accumulateYValues(_planningTarget),
        planningLowTarget: accumulateYValues(_planningLowTarget),
        planningHighTarget: accumulateYValues(_planningHighTarget),
      };
    }, [data.dailyMetrics, yesterday]);

  return {
    dateLabels,
    datasets: [
      {
        label: ACTUAL_ID,
        id: ACTUAL_ID,
        data: actualData,
        borderColor: ACTUAL_COLOR,
        backgroundColor: ACTUAL_COLOR,
        yAxisID: "y",
        hidden: !datasetVisibility[ACTUAL_ID],
        pointRadius: 0,
        borderWidth: 1.5,
        tension: 0.5,
        animation: false,
      },
      {
        label: PLANNING_TARGET_ID,
        id: PLANNING_TARGET_ID,
        data: planningTarget,
        borderColor: PLANNING_TARGET_COLOR,
        backgroundColor: PLANNING_TARGET_COLOR,
        yAxisID: "y",
        hidden: !datasetVisibility[PLANNING_TARGET_ID],
        pointRadius: 0,
        borderWidth: 1.5,
        tension: 0.5,
        animation: false,
      },
      {
        label: TARGET_RANGE_ID,
        id: TARGET_RANGE_ID,
        data: planningLowTarget,
        backgroundColor: TARGET_RANGE_COLOR,
        fill: "+1",
        yAxisID: "y",
        hidden: !datasetVisibility[TARGET_RANGE_ID],
        pointRadius: 0,
        borderWidth: 0,
        tension: 0.5,
        animation: false,
      },
      {
        label: TARGET_RANGE_ID,
        id: TARGET_RANGE_ID,
        data: planningHighTarget,
        yAxisID: "y",
        hidden: !datasetVisibility[TARGET_RANGE_ID],
        pointRadius: 0,
        borderWidth: 0,
        tension: 0.5,
        animation: false,
      },
    ],
  };
}

export function useBuildLiveForecastKpiDataset({
  data,
  datasetVisibility,
}: {
  data: FetchLiveForecastTrackingData_me_mmm_performanceTracking_liveForecast["data"];
  datasetVisibility: Record<string, boolean>;
}) {
  const { user } = useUser();
  const dateLabels =
    data.map(({ date }) => `${moment(date).format("MMM DD, YYYY")}`) ?? [];

  const yesterday = useYesterday();

  const { adoptedKpiName } = useAdoptedBudgetMix();

  const { forecastKPIData } = useMemo(() => {
    const _forcastKPIData: {
      y: number | null;
      x: number;
      nullify?: boolean;
    }[] = [];

    data.forEach((row, index) => {
      const kpi = row.kpis.find((k) => k.key === adoptedKpiName);

      _forcastKPIData.push({
        y: kpi?.value ?? null,
        x: index,
        nullify: moment.tz(row.date, user.timezone).isBefore(yesterday),
      });
    });

    return {
      forecastKPIData: accumulateYValues(_forcastKPIData),
    };
  }, [adoptedKpiName, data, yesterday, user.timezone]);

  return {
    dateLabels,
    datasets: [
      {
        label: LIVE_FORECAST_ID,
        id: LIVE_FORECAST_ID,
        data: forecastKPIData,
        borderColor: LIVE_FORECAST_COLOR,
        backgroundColor: LIVE_FORECAST_COLOR,
        yAxisID: "y",
        hidden: !datasetVisibility[LIVE_FORECAST_ID],
        pointRadius: 0,
        borderWidth: 1.5,
        tension: 0.5,
        animation: false,
        borderDash: LIVE_FORECAST_BORDER_DASH,
      },
    ],
  };
}
